"use client"

// app/error.tsx
// Root-level error boundary with branded error page.
// Follows Canopy design system (muted, primary, foreground colors).
import { useEffect } from "react"
import Link from "next/link"

interface ErrorProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function RootError({ error, reset }: ErrorProps) {
  useEffect(() => {
    // Log error to an error reporting service
    console.error("Root error boundary caught:", error)
  }, [error])

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md p-8 rounded-md bg-muted border border-border shadow-sm flex flex-col gap-6 items-center text-center">
        {/* Logo mark */}
        <div className="w-10 h-10 rounded-md bg-destructive/20 flex items-center justify-center">
          <span className="text-destructive font-bold text-lg">!</span>
        </div>

        <div>
          <h1 className="text-xl font-semibold text-foreground">Something went wrong</h1>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            We encountered an unexpected error. Please try again.
          </p>
          {error.digest && (
            <p className="text-xs text-muted-foreground mt-2 font-mono">
              Error ID: {error.digest}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2 w-full">
          <button
            onClick={reset}
            className="w-full inline-flex items-center justify-center rounded-md px-4 py-2.5 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Try again
          </button>

          <Link
            href="/dashboard"
            className="w-full inline-flex items-center justify-center rounded-md px-4 py-2.5 text-sm font-medium bg-card text-foreground hover:bg-muted transition-colors"
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    </div>
  )
}
